/** Display rules for Quantis Voice: UK number formatting, durations and call labels. */
import type { CallOutcome, VoiceCallRecord } from '../../services/VoiceService';

/** E.164 from what someone typed: "07700 900123", "+44 7700 900123", "0044…". Null if it cannot be a number. */
export function normaliseDialled(raw: string): string | null {
  const trimmed = raw.trim().replace(/^(\+44\s*)\(0\)/, '$1');
  let digits = trimmed.replace(/[^\d]/g, '');
  if (!digits) return null;
  if (trimmed.startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  if (digits.startsWith('00')) {
    digits = digits.slice(2);
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (digits.startsWith('44') && digits.length >= 11) return `+${digits}`;
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits.length >= 9 && digits.length <= 10 ? `+44${digits}` : null;
}

/** "+447700900123" reads as "07700 900123"; "+441134960000" as "0113 496 0000". */
export function formatPhoneNumber(e164: string | null | undefined): string {
  if (!e164) return 'Unknown number';
  if (/^(anonymous|restricted|unknown)$/i.test(e164) || e164 === '+266696687') return 'Withheld number';
  if (!e164.startsWith('+44')) return e164;
  const national = `0${e164.slice(3)}`;
  if (national.length !== 11) return national;
  if (/^07/.test(national) || /^0[3589]/.test(national)) return `${national.slice(0, 5)} ${national.slice(5)}`;
  if (/^02/.test(national)) return `${national.slice(0, 3)} ${national.slice(3, 7)} ${national.slice(7)}`;
  if (/^01\d1/.test(national) || /^011/.test(national)) return `${national.slice(0, 4)} ${national.slice(4, 7)} ${national.slice(7)}`;
  return `${national.slice(0, 5)} ${national.slice(5)}`;
}

export function formatCallDuration(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(seconds ?? 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

const OUTCOME_LABELS: Record<CallOutcome, string> = {
  answered: 'Answered',
  missed: 'Missed',
  voicemail: 'Voicemail',
  no_answer: 'No answer',
  busy: 'Busy',
  failed: 'Did not connect',
  cancelled: 'Cancelled',
};

export function callOutcomeLabel(call: Pick<VoiceCallRecord, 'direction' | 'outcome' | 'status'>): string {
  if (call.outcome) return OUTCOME_LABELS[call.outcome];
  return ['ringing', 'in-progress', 'initiated', 'queued'].includes(call.status) ? 'In progress' : (call.direction === 'inbound' ? 'Incoming' : 'Outgoing');
}

/** A missed inbound call is the one the list should make stand out. */
export const isMissedCall = (call: Pick<VoiceCallRecord, 'direction' | 'outcome'>) =>
  call.direction === 'inbound' && (call.outcome === 'missed' || call.outcome === 'voicemail');

export function callerLabel(contact: { name: string; company: string | null } | null, number: string): { title: string; subtitle: string } {
  if (contact) return { title: contact.name, subtitle: contact.company ? `${contact.company} · ${formatPhoneNumber(number)}` : formatPhoneNumber(number) };
  return { title: formatPhoneNumber(number), subtitle: '' };
}

/** "Today 09:41", "Yesterday 16:05", or "Mon 3 Nov 16:05" in UK form. */
export function formatCallTime(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
  const day = (d: Date) => d.toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
  if (day(at) === day(now)) return `Today ${time}`;
  if (day(at) === day(new Date(now.getTime() - 86_400_000))) return `Yesterday ${time}`;
  return `${at.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/London' })} ${time}`;
}

/** Keys on the dial pad and what each sends as a tone. */
export const DIAL_PAD: Array<{ key: string; letters: string }> = [
  { key: '1', letters: '' }, { key: '2', letters: 'ABC' }, { key: '3', letters: 'DEF' },
  { key: '4', letters: 'GHI' }, { key: '5', letters: 'JKL' }, { key: '6', letters: 'MNO' },
  { key: '7', letters: 'PQRS' }, { key: '8', letters: 'TUV' }, { key: '9', letters: 'WXYZ' },
  { key: '*', letters: '' }, { key: '0', letters: '+' }, { key: '#', letters: '' },
];
